<template>
  <div class="min-h-[100dvh] overflow-y-auto flex flex-col items-center justify-start bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-2 sm:p-4">
    <!-- Logo estático -->
    <div class="mt-0 sm:mt-2 -mb-10 text-center flex flex-col items-center flex-shrink-0">
      <div class="flex items-center justify-center">
        <img 
          src="@/assets/logo/jj.png" 
          alt="SYSTEM JJ Logo" 
          class="w-80 sm:w-[28rem] md:w-[36rem] lg:w-[42rem] h-auto max-h-[28vh] object-contain"
          @error="handleImageError"
        />
      </div>
    </div>

    <!-- Formulario de Login -->
    <div class="w-full max-w-md -mt-1 mb-4 bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden flex-shrink-0">
      <div class="p-4 sm:p-6">
        <div class="mb-2 sm:mb-4 text-center">
          <h2 class="text-lg sm:text-xl font-bold text-gray-800 dark:text-white">
            Iniciar Sesión
          </h2>
          <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-1">
            Ingresa tus credenciales para continuar
          </p>
        </div>

        <form @submit.prevent="handleLogin">
          <!-- Usuario -->
          <div class="mb-3 sm:mb-4">
            <label for="username" class="block mb-1 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
              Usuario o Email
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input
                id="username"
                v-model="form.username"
                @input="error = ''"
                type="text"
                required
                class="pl-10 w-full px-3 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors text-sm"
                placeholder="Ingresa tu usuario o email"
                autocomplete="username"
              />
            </div>
          </div>

          <!-- Contraseña -->
          <div class="mb-3 sm:mb-4">
            <label for="password" class="block mb-1 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
              Contraseña
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                id="password"
                v-model="form.password"
                @input="error = ''"
                :type="showPassword ? 'text' : 'password'"
                required
                class="pl-10 pr-10 w-full px-3 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors text-sm"
                placeholder="Ingresa tu contraseña"
                autocomplete="current-password"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg v-if="!showPassword" class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              </button>
            </div>
          </div>

          <div class="flex items-center justify-between mb-3 sm:mb-4">
            <div class="flex items-center">
              <input
                id="remember"
                v-model="form.remember"
                type="checkbox"
                class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label for="remember" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Recordarme
              </label>
            </div>
            <button
              type="button"
              @click="$router.push('/forgot-password')"
              class="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2 sm:py-2.5 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
          >
            <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span v-if="!loading">Iniciar Sesión</span>
            <span v-else>Iniciando...</span>
          </button>
          
          <div v-if="error" class="mt-3 text-center">
            <div class="p-3 rounded-lg" :class="errorType === 'inactive' ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'">
              <p class="text-sm" :class="errorType === 'inactive' ? 'text-yellow-700 dark:text-yellow-300' : 'text-red-600 dark:text-red-400'">
                {{ error }}
              </p>
            </div>
          </div>
        </form>
      </div>
      <!-- ❌ ELIMINADO: bloque "¿No tienes una cuenta? Regístrate aquí" -->
    </div>

    <!-- Footer con enlace a sistemas-jj.com -->
    <div class="mt-2 text-center animate-fade-in" style="animation-delay: 0.5s;">
      <div class="flex flex-col items-center gap-2">
        <div class="flex items-center gap-2 text-[10px] text-slate-400 dark:text-gray-500 uppercase tracking-[0.2em] font-bold">
          <span>Plataforma de Gestión</span>
          <span class="w-1 h-1 rounded-full bg-slate-300 dark:bg-gray-700"></span>
          <span>v1.0.0</span>
        </div>
        <div class="flex items-center justify-center gap-1.5 text-xs text-slate-500 dark:text-gray-400">
          <span class="opacity-80">Desarrollado con</span>
          <svg class="w-3.5 h-3.5 text-rose-500 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
          <span class="opacity-80">por</span>
          <!-- ✅ CONVERTIDO EN ENLACE -->
          <a
            href="https://www.sistemas-jj.com"
            target="_blank"
            rel="noopener noreferrer"
            class="font-black text-indigo-600 dark:text-indigo-400 tracking-tight hover:text-indigo-800 dark:hover:text-indigo-300 hover:underline transition-all"
          >
            SYSTEM JJ
          </a>
        </div>
      </div>
    </div>

    <button
      @click="toggleDarkMode"
      class="fixed top-4 right-4 p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-shadow"
      title="Cambiar tema"
    >
      <svg v-if="darkMode" class="h-6 w-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
      <svg v-else class="h-6 w-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    </button>

    <!-- ===================================================== -->
    <!-- MODAL: EMPRESA DESACTIVADA -->
    <!-- ===================================================== -->
    <div
      v-if="mostrandoModalDesactivada"
      class="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style="background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(8px);"
    >
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-modal-in">
        <!-- Header -->
        <div class="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-5 flex items-center gap-4">
          <div class="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h2 class="text-xl font-bold text-white">Acceso Bloqueado</h2>
            <p class="text-sm text-white/90">Su mensualidad ha expirado</p>
          </div>
        </div>

        <!-- Contenido -->
        <div class="p-6 space-y-5">
          <div class="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-lg p-4">
            <p class="text-sm text-red-800 dark:text-red-200 leading-relaxed">
              {{ mensajeDesactivada }}
            </p>
          </div>

          <div class="text-center">
            <p class="text-xs uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 mb-3">
              Contáctenos para renovar
            </p>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href="https://wa.me/584167594723?text=Hola,%20necesito%20renovar%20mi%20mensualidad%20del%20sistema"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors shadow-sm"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                0416-7594723
              </a>
              <a
                href="https://wa.me/584145572936?text=Hola,%20necesito%20renovar%20mi%20mensualidad%20del%20sistema"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors shadow-sm"
              >
                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                0414-5572936
              </a>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            @click="cerrarModalDesactivada"
            class="px-5 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL DE SELECCIÓN DE EMPRESAS -->
    <div 
      v-if="mostrandoSeleccionEmpresa" 
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      style="background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(8px);"
      @click.self="cerrarSelectorEmpresas"
    >
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-modal-in">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-bold text-gray-800 dark:text-white">
                Selecciona tu empresa
              </h3>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Tienes acceso a múltiples empresas
              </p>
            </div>
          </div>
          <button
            @click="cerrarSelectorEmpresas"
            class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="p-4 space-y-2">
          <div
            v-for="(empresa, index) in empresasDisponibles"
            :key="index"
            @click="seleccionarEmpresa(empresa)"
            class="flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-all group"
          >
            <div class="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {{ (empresa.nombre || 'E').charAt(0).toUpperCase() }}
            </div>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-gray-800 dark:text-white truncate">
                {{ empresa.nombre || 'Empresa' }}
              </p>
              <p class="text-sm text-gray-500 dark:text-gray-400 truncate">
                ID: {{ empresa.tenantId || empresa.slug || empresa.id || 'N/A' }}
              </p>
            </div>
            <svg class="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        <div class="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            @click="cerrarSelectorEmpresas"
            class="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loginUsuario, limpiarEmpresas } from '@/api/usuarios.js';
import { setAuth, clearAuth, isAuthenticated, getCurrentTenant, setCurrentTenant } from '@/api/index.js';
import { cargarConfigTenant } from '@/utils/tenantConfig';

export default {
  name: 'LoginPage',
  data() {
    return {
      form: {
        username: '',
        password: '',
        remember: false
      },
      showPassword: false,
      loading: false,
      error: '',
      errorType: 'general',
      darkMode: false,
      logoError: false,
      mostrandoSeleccionEmpresa: false,
      empresasDisponibles: [],
      resolviendoSeleccion: null,

      mostrandoModalDesactivada: false,
      mensajeDesactivada: 'Su mensualidad ha expirado. Para renovar, contáctenos al WhatsApp 0416-7594723 o al 0414-5572936.'
    };
  },
  mounted() {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    this.darkMode = savedDarkMode;
    this.applyDarkMode(savedDarkMode);

    const savedUsername = localStorage.getItem('rememberedUsername');
    if (savedUsername) {
      this.form.username = savedUsername;
      this.form.remember = true;
    }
    
    const userData = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (userData && token && isAuthenticated()) {
      setTimeout(() => {
        this.$router.push('/bienvenida');
      }, 100);
    }
    
    const tenant = getCurrentTenant();
    if (tenant === 'SYSTEM JJ' || tenant.includes(' ')) {
      console.warn('⚠️ Tenant inválido detectado, reseteando a default');
      setCurrentTenant('default');
    }
  },
  methods: {
    async handleLogin() {
      this.error = '';
      this.errorType = 'general';
      this.mostrandoSeleccionEmpresa = false;
      
      if (!this.form.username.trim()) {
        this.error = 'Por favor ingresa tu usuario o email';
        this.errorType = 'general';
        return;
      }
      
      if (!this.form.password) {
        this.error = 'Por favor ingresa tu contraseña';
        this.errorType = 'general';
        return;
      }

      this.loading = true;

      try {
        let tenantId = getCurrentTenant() || 'default';
        
        if (tenantId === 'SYSTEM JJ' || tenantId.includes(' ') || tenantId.length > 50) {
          console.warn('⚠️ Tenant inválido, usando default:', tenantId);
          tenantId = 'default';
          setCurrentTenant(tenantId);
        }
        
        console.log('🔐 Login con tenant:', tenantId);

        const response = await loginUsuario(
          this.form.username, 
          this.form.password, 
          tenantId, 
          false
        );

        if (!response.success && response.type === 'empresa_desactivada') {
          console.log('⛔ Empresa desactivada, mostrando modal');
          this.mensajeDesactivada = response.error ||
            'Su mensualidad ha expirado. Para renovar, contáctenos al WhatsApp 0416-7594723 o al 0414-5572936.';
          this.mostrandoModalDesactivada = true;
          this.loading = false;
          return;
        }

        if (response.success && response.requiereSeleccionEmpresa === true) {
          console.log('🏢 Usuario con múltiples empresas, mostrando selector');
          
          this.empresasDisponibles = response.empresasDisponibles;
          this.mostrandoSeleccionEmpresa = true;
          this.loading = false;
          
          const seleccionada = await new Promise((resolve) => {
            this.resolviendoSeleccion = resolve;
          });
          
          if (seleccionada) {
            console.log('🏢 Empresa seleccionada:', seleccionada);
            this.loading = true;
            this.mostrandoSeleccionEmpresa = false;
            
            setCurrentTenant(seleccionada);
            
            const response2 = await loginUsuario(
              this.form.username, 
              this.form.password, 
              seleccionada, 
              true
            );

            if (!response2.success && response2.type === 'empresa_desactivada') {
              this.mensajeDesactivada = response2.error ||
                'Su mensualidad ha expirado. Para renovar, contáctenos al WhatsApp 0416-7594723 o al 0414-5572936.';
              this.mostrandoModalDesactivada = true;
              this.loading = false;
              return;
            }
            
            if (response2.success && response2.data) {
              await this.procesarLoginExitoso(response2.data);
            } else {
              this.handleLoginError(response2);
              this.loading = false;
            }
          } else {
            this.loading = false;
            this.mostrandoSeleccionEmpresa = false;
          }
          return;
        }

        if (response.success && response.data) {
          await this.procesarLoginExitoso(response.data);
        } else {
          this.handleLoginError(response);
        }
        
      } catch (error) {
        console.error('❌ Error en login:', error);
        this.handleLoginError(error);
      } finally {
        if (!this.mostrandoSeleccionEmpresa) {
          this.loading = false;
        }
      }
    },

    seleccionarEmpresa(empresa) {
      console.log('📋 Datos de la empresa seleccionada:', empresa);
      
      let tenantId = empresa.tenantId || empresa.TenantId 
                  || empresa.slug || empresa.Slug 
                  || empresa.id || empresa.Id 
                  || null;
      
      if (!tenantId) {
        if (empresa.nombre === 'SYSTEM JJ') {
          tenantId = 'default';
        } else {
          tenantId = empresa.nombre?.toLowerCase().replace(/[^a-z0-9_-]/g, '') || 'default';
        }
      }
      
      console.log('✅ Tenant ID extraído:', tenantId);
      
      if (this.resolviendoSeleccion) {
        this.resolviendoSeleccion(tenantId);
        this.resolviendoSeleccion = null;
      }
    },

    cerrarSelectorEmpresas() {
      console.log('❌ Selección cancelada');
      if (this.resolviendoSeleccion) {
        this.resolviendoSeleccion(null);
        this.resolviendoSeleccion = null;
      }
      this.mostrandoSeleccionEmpresa = false;
    },

    cerrarModalDesactivada() {
      this.mostrandoModalDesactivada = false;
      this.form.password = '';
    },

    async procesarLoginExitoso(userData) {
      let tenantToSave = 'default';
      if (userData.empresaSeleccionada) {
        tenantToSave = userData.empresaSeleccionada.tenantId 
                    || userData.empresaSeleccionada.slug 
                    || userData.empresaSeleccionada.id 
                    || 'default';
      } else {
        tenantToSave = getCurrentTenant() || 'default';
      }
      
      if (tenantToSave.includes(' ') || tenantToSave === 'SYSTEM JJ') {
        tenantToSave = 'default';
      }
      
      setCurrentTenant(tenantToSave);
      console.log('🏢 Tenant guardado:', tenantToSave);
      
      const userToStore = {
        idUsuario: userData.idUsuario,
        nombreUsuario: userData.nombreUsuario,
        nombre: userData.nombre,
        apellido: userData.apellido,
        email: userData.email,
        telefono: userData.telefono || '',
        idRol: userData.rol?.idRol,
        nombreRol: userData.rol?.nombre,
        token: userData.token,
        tenantId: tenantToSave,
        timestamp: new Date().toISOString()
      };
      
      setAuth(userData.token, userToStore, this.form.remember);
      
      if (this.form.remember) {
        localStorage.setItem('rememberedUsername', this.form.username);
      }

      localStorage.setItem('tenantId', tenantToSave);
      
      console.log(`✅ Bienvenido ${userData.nombre}!`);

      try {
        const config = await cargarConfigTenant(tenantToSave);
        if (config) {
          console.log('✅ Config del tenant cargada:', config);
        }
      } catch (err) {
        console.warn('Error cargando config del tenant:', err);
      }

      window.dispatchEvent(new CustomEvent('userLoggedIn', {
        detail: { tenantId: tenantToSave }
      }));
      
      setTimeout(() => {
        this.$router.push('/bienvenida');
      }, 500);
    },

    handleLoginError(response) {
      if (response?.type === 'empresa_desactivada' || response?.codigo === 'EMPRESA_DESACTIVADA') {
        this.mensajeDesactivada = response.error ||
          'Su mensualidad ha expirado. Para renovar, contáctenos al WhatsApp 0416-7594723 o al 0414-5572936.';
        this.mostrandoModalDesactivada = true;
        this.error = '';
        this.errorType = 'general';
        clearAuth();
        return;
      }

      const errorMessage = response.error || response.message || 'Credenciales incorrectas';
      this.error = errorMessage;
      
      const errorType = response.type || 'general';
      
      if (errorType === 'inactive' || errorMessage.toLowerCase().includes('inactivo')) {
        this.errorType = 'inactive';
      } else if (errorType === 'password' || errorMessage.toLowerCase().includes('contraseña')) {
        this.errorType = 'password';
      } else if (errorType === 'notfound' || errorMessage.toLowerCase().includes('no encontrado')) {
        this.errorType = 'notfound';
      } else if (errorType === 'network' || errorMessage.toLowerCase().includes('conexión')) {
        this.errorType = 'network';
      } else if (errorType === 'tenant_error' || errorMessage.toLowerCase().includes('empresa')) {
        this.errorType = 'tenant_error';
        setCurrentTenant('default');
      }
      
      clearAuth();
    },

    clearSessionData() {
      clearAuth();
      limpiarEmpresas();
      setCurrentTenant('default');
      localStorage.removeItem('tenantId');
      localStorage.removeItem('tenantConfig');
      window.dispatchEvent(new CustomEvent('userLoggedOut'));
    },

    toggleDarkMode() {
      this.darkMode = !this.darkMode;
      this.applyDarkMode(this.darkMode);
      localStorage.setItem('darkMode', this.darkMode.toString());
    },

    applyDarkMode(isDark) {
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    },

    handleImageError() {
      this.logoError = true;
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes modalIn {
  from { opacity: 0; transform: scale(0.95) translateY(20px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-out forwards;
}

.animate-modal-in {
  animation: modalIn 0.3s ease-out forwards;
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.dark ::-webkit-scrollbar-track {
  background: #1f2937;
}

.dark ::-webkit-scrollbar-thumb {
  background: #4b5563;
}
</style>